import React from "react";

import { ReviewChangesPanel } from "../ReviewChangesPanel";
import type { SkillCollectionReviewItem } from "../store";

import type { SkillCollectionSavePhase } from "./hooks/useEditorSaveReview";
import { useVersionReviewState } from "./hooks/useVersionReviewState";

import { Button } from "@/components/ui/button";
import CommitMessage from "@/components/ui/CommitMessageDialog/CommitMessage";
import { DialogFooter } from "@/components/ui/dialog";
import { PhasedReviewDialog } from "@/components/ui/VersionReviewDialog/PhasedReviewDialog";

interface VersionReviewDialogProps {
  isOpen: boolean;
  reviewItems: SkillCollectionReviewItem[];
  versionFiles: Record<string, string> | null;
  isLoadingVersionSnapshot?: boolean;
  isSaving: boolean;
  savePhase: SkillCollectionSavePhase;
  onClose: () => void;
  onConfirm: (changeSummary?: string) => void;
}

export const VersionReviewDialog = ({
  isOpen,
  reviewItems,
  versionFiles,
  isLoadingVersionSnapshot = false,
  isSaving,
  savePhase,
  onClose,
  onConfirm,
}: VersionReviewDialogProps) => {
  const {
    changeSummary,
    setChangeSummary,
    originalContentByPath,
    isDiffsReady,
    isLoadingOriginals,
    handleSubmit,
  } = useVersionReviewState({
    isOpen,
    reviewItems,
    versionFiles,
    isLoadingVersionSnapshot,
    onConfirm,
  });

  const isStatusPhase = savePhase !== "review";

  return (
    <PhasedReviewDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Save New Skill Collection Version"
      phase={savePhase}
      statusTitle={savePhase === "saving" ? "Saving skill collection" : "Version saved"}
      statusDescription={
        savePhase === "saving"
          ? "Creating a new version from your latest changes."
          : "Your new skill collection version is ready. Opening it now."
      }
      reviewContent={
        <form onSubmit={handleSubmit} aria-hidden={isStatusPhase} className="flex flex-col gap-y-4">
          <CommitMessage
            message={changeSummary}
            setMessage={setChangeSummary}
            label="Skill collection version change message (Optional)"
          />

          <ReviewChangesPanel
            reviewItems={reviewItems}
            description="Review these draft changes before creating a new version."
            isReady={isDiffsReady}
            isLoadingNoticeVisible={isLoadingOriginals}
            loadingMessage={
              isLoadingOriginals
                ? "Loading version snapshot for diff preview..."
                : "Preparing review..."
            }
            originalContentByPath={originalContentByPath}
            isLoadingOriginals={isLoadingOriginals}
          />

          <DialogFooter>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving || isLoadingOriginals || !isDiffsReady}>
                Save Version
              </Button>
            </div>
          </DialogFooter>
        </form>
      }
    />
  );
};
